// src/sectors/pilates/settings/pages/PilatesSettingsPage.jsx
import React from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';

import { CompanySettingsTab } from '../tabs/CompanySettingsTab';
import { NotificationsSettingsTab } from '../tabs/NotificationsSettingsTab';
import { AccountSecurityTab } from '../tabs/AccountSecurityTab';

export default function PilatesSettingsPage() {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const [tab, setTab] = React.useState(0);

  const headerBg = isDark
    ? `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.16)}, ${alpha('#000', 0)})`
    : `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha('#fff', 0.9)})`;

  const shellBg = isDark ? alpha('#0b1220', 0.22) : alpha('#fff', 0.72);
  const shellBorder = alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1);

  const labels = ['İşletme', 'Bildirim', 'Kullanıcı adı & Şifre'];

  return (
    <MDBox sx={{ width: '100%', mt: 2 }}>
      <Card
        sx={{
          borderRadius: { xs: '18px', md: '22px' },
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[8],
          overflow: 'hidden',
        }}
      >
        {/* ✅ softer + mobile friendly header */}
        <MDBox
          sx={{
            px: { xs: 1, md: 1.5 },
            pt: { xs: 1, md: 1.25 },
            pb: { xs: 0.9, md: 1.1 },
            background: headerBg,
          }}
        >
          <MDBox
            sx={{
              p: { xs: 0.45, md: 0.6 },
              borderRadius: { xs: '16px', md: '18px' },
              border: `1px solid ${shellBorder}`,
              bgcolor: shellBg,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons={false} // ✅ mobilde daha iyi (swipe)
              allowScrollButtonsMobile={false}
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: { xs: 36, md: 40 },
                '& .MuiTabs-flexContainer': { gap: { xs: 6, md: 8 } },
                '& .MuiTabs-scroller': {
                  // swipe hissi yumuşak
                  scrollBehavior: 'smooth',
                },
              }}
            >
              {labels.map((label, idx) => {
                const selected = tab === idx;

                return (
                  <Tab
                    key={label}
                    label={label}
                    disableRipple
                    sx={{
                      minHeight: { xs: 36, md: 40 },
                      minWidth: 'auto', // ✅ mobilde sıkışmasın
                      px: { xs: 1.25, md: 1.6 },
                      borderRadius: '999px',
                      textTransform: 'none',
                      fontWeight: 900,
                      fontSize: { xs: 12.5, md: 13.5 },
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',

                      color: selected ? theme.palette.info.main : theme.palette.text.secondary,
                      bgcolor: selected
                        ? alpha(theme.palette.info.main, isDark ? 0.2 : 0.1)
                        : 'transparent',

                      border: `1px solid ${
                        selected
                          ? alpha(theme.palette.info.main, isDark ? 0.32 : 0.2)
                          : alpha(theme.palette.text.primary, isDark ? 0.1 : 0.07)
                      }`,

                      boxShadow: selected ? theme.shadows[1] : 'none',
                      transition: 'all .14s ease',

                      '&:hover': {
                        bgcolor: selected
                          ? alpha(theme.palette.info.main, isDark ? 0.24 : 0.12)
                          : alpha(theme.palette.text.primary, isDark ? 0.06 : 0.03),
                      },

                      '&.Mui-focusVisible': {
                        outline: `2px solid ${alpha(theme.palette.info.main, 0.35)}`,
                        outlineOffset: '2px',
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </MDBox>
        </MDBox>

        <Divider />

        <MDBox sx={{ p: { xs: 0.5, md: 0.3 } }}>
          {tab === 0 ? <CompanySettingsTab /> : null}
          {tab === 1 ? <NotificationsSettingsTab /> : null}
          {tab === 2 ? <AccountSecurityTab /> : null}
        </MDBox>
      </Card>
    </MDBox>
  );
}
